import { backButton, init, initData, miniApp, themeParams, viewport } from '@tma.js/sdk-vue';
import { disableTelegramVerticalSwipe } from './swipeBehavior';

export const LIGHT_BG = '#eef2f4';
export const DARK_BG = '#0c191d';

export interface TelegramContext {
  rawInitData: string;
  languageCode: string | undefined;
}

/**
 * HSP perceived-brightness check on a #rrggbb colour; Telegram gives us bg_color, we decide
 * the theme from it. The formula and the 120 threshold mirror `@tma.js/sdk`'s own `isColorDark`
 * helper (node_modules/@tma.js/sdk/dist/index.js, function `ks`, ~line 1791) rather than a naive
 * raw-channel average, which agrees at the extremes but misclassifies mid-tone theme backgrounds
 * (e.g. `#517da2` reads as light without the gamma weighting, and as dark with it).
 * Do not "simplify" these constants without re-checking that helper.
 */
export function isDarkColor(hex: string | undefined): boolean {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return false;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const perceivedBrightness = Math.sqrt(0.299 * r! ** 2 + 0.587 * g! ** 2 + 0.114 * b! ** 2);
  return perceivedBrightness < 120;
}

/**
 * Brand colours stay fixed; only light/dark follows Telegram. Header and background match the
 * page. Pushing the native chrome colour is best-effort: `setHeaderColor`/`setBgColor` require
 * the Mini App component to be mounted (@tma.js/sdk dist/index.js:2256 gates both on
 * `this.isMounted`), which is not guaranteed at every call site (e.g. the `bgColor.sub`
 * callback firing on a host that rejected `miniApp.mount()`), so a failure here must never stop
 * the in-app `dark` class — which already applied — from taking effect. The two calls are
 * guarded independently so one failing does not skip the other.
 */
export function applyTheme(dark: boolean): void {
  document.documentElement.classList.toggle('dark', dark);
  const bg = dark ? DARK_BG : LIGHT_BG;
  try {
    miniApp.setHeaderColor(bg);
  } catch (error) {
    console.error(
      'Failed to push the Telegram header colour; the in-app theme still applies.',
      error,
    );
  }
  try {
    miniApp.setBgColor(bg);
  } catch (error) {
    console.error(
      'Failed to push the Telegram background colour; the in-app theme still applies.',
      error,
    );
  }
}

/**
 * Initialises the SDK and mounts each Telegram-host component in its own failure domain: an old
 * client or an odd host rejecting one component (e.g. `themeParams.mount()`) must not stop
 * `init()`/`initData.restore()` from running or block the `{ rawInitData, languageCode }` this
 * function returns, since Task 9 calls it at module scope before the app mounts.
 *
 * The initial theme is applied only after `miniApp.mount()` has run: `themeParams.bgColor()`
 * itself is only populated once `themeParams.mount()` has restored state (dist/index.js:1912
 * initialises the internal store to `{}`, populated in `onMounted` at dist/index.js:1920-1922),
 * and pushing the colour to Telegram needs `miniApp` mounted too (see `applyTheme`), so this must
 * run after both mounts, not from inside the theme group.
 */
/**
 * Only the main Mini App (the "Open" button in the chat list, `?startapp` links) honours
 * BotFather's fullscreen setting; the menu button next to the input field and inline buttons
 * open the Mini App at half height. So the app asks for the size itself: fullscreen where the
 * client supports it (Bot API 8.0+; an `ALREADY_FULLSCREEN` answer is swallowed by the SDK),
 * otherwise plain `expand()`. Best effort: a host refusing both leaves the pre-existing layout.
 */
export function fillScreen(): void {
  try {
    if (viewport.isFullscreen()) return;
    if (!viewport.requestFullscreen.isAvailable()) {
      viewport.expand.ifAvailable();
      return;
    }
    void viewport.requestFullscreen().catch((error: unknown) => {
      console.error('Telegram refused fullscreen; expanding to the maximum height instead.', error);
      viewport.expand.ifAvailable();
    });
  } catch (error) {
    console.error('Failed to resize the Telegram Mini App.', error);
  }
}

export function setupTelegram(): TelegramContext {
  init();
  initData.restore();

  try {
    themeParams.mount();
    themeParams.bindCssVars();
    themeParams.bgColor.sub((color) => applyTheme(isDarkColor(color)));
  } catch (error) {
    console.error('Failed to mount Telegram theme params; falling back to the light theme.', error);
  }

  try {
    miniApp.mount();
    miniApp.ready();
  } catch (error) {
    console.error('Failed to mount the Telegram Mini App component.', error);
  }

  // Relies on applyTheme's own internal guards (above) rather than a redundant try/catch here:
  // isDarkColor is a pure function and themeParams.bgColor() is a plain state getter, neither of
  // which can throw, so nothing here can stop the return below.
  applyTheme(isDarkColor(themeParams.bgColor()));

  try {
    if (backButton.isSupported()) backButton.mount();
  } catch (error) {
    console.error('Failed to mount the Telegram back button.', error);
  }

  disableTelegramVerticalSwipe();

  // Exposes --tg-viewport-safe-area-inset-* and --tg-viewport-content-safe-area-inset-* so the
  // layout can stay clear of the status bar and of Telegram's own header buttons (App.vue).
  // Mounting is async (it asks the host for the current insets); the variables stay at their
  // CSS fallbacks of 0 until then, which is the pre-existing layout. The size request waits for
  // the mount so `isFullscreen` reflects how the host launched us.
  try {
    void viewport
      .mount()
      .then(() => {
        viewport.bindCssVars();
        fillScreen();
      })
      .catch((error: unknown) => {
        console.error('Failed to mount the Telegram viewport; safe-area insets stay at 0.', error);
      });
  } catch (error) {
    console.error('Failed to mount the Telegram viewport; safe-area insets stay at 0.', error);
  }

  return { rawInitData: initData.raw() ?? '', languageCode: initData.user()?.language_code };
}
